import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { bapi } from '@/lib/billingApi';
import { EditPlanDialog } from './EditPlanDialog';
import { UserRow } from './UserRow';

const HEAD = ['User', 'Subscription', 'Joined'];

export const UsersTable = () => {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ users: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (query) => {
    setLoading(true);
    try { setData(await bapi('/admin/users', { query: { limit: 50, q: query } })); } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  const remove = async (u) => {
    if (!window.confirm(`Are you sure you want to delete ${u.email}? This action cannot be undone and will permanently remove all user data.`)) return;
    try {
      await bapi(`/admin/users/${u.user_id}`, { method: 'DELETE' });
      toast.success(`User ${u.email} and all associated data deleted`);
      load(q.trim());
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card className="lg:col-span-3 border-slate-200">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
        <CardTitle className="text-lg flex items-center gap-2" data-testid="users-total">Users ({data.total}){loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}</CardTitle>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" data-testid="users-search-input" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                {HEAD.map((h) => <th key={h} className="text-left py-3 px-4 text-sm font-medium text-slate-500">{h}</th>)}
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => <UserRow key={u.user_id} u={u} onEdit={setEditing} onDelete={remove} />)}
            </tbody>
          </table>
          {!loading && data.users.length === 0 && <p className="text-slate-500 text-center py-8" data-testid="users-empty">No users match "{q}"</p>}
          {data.total > data.users.length && <p className="text-xs text-slate-400 text-center pt-3">Showing {data.users.length} of {data.total}. Search to find someone else.</p>}
        </div>
      </CardContent>
      {editing && <EditPlanDialog user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(q.trim()); }} />}
    </Card>
  );
};

